/**
 * HoverHandoff — glide from one hovered card straight onto its neighbour.
 *
 * A catalog's hover preview is a popup that covers the card it came from AND
 * the cards around it, so the pointer moving to a neighbour never reaches that
 * neighbour's `mouseenter`. The player has to leave the grid and come back to
 * inspect the card sitting right next to the one they are looking at.
 *
 * The preview therefore watches its own pointermove, asks the document what is
 * underneath, and hands the hover to the first matching card it finds.
 *
 * Shared by Collection, Deck Builder, Moon Exchange and Moon Dream so the four
 * catalogs feel identical — they previously differed only because the behaviour
 * lived inline in one of them.
 */
import { TouchPreview } from './touch-preview.js';
let HoverHandoff = {};

(function () {

    /* A HAND-OFF, NOT A TEAR-DOWN. The hand-off used to `clear()` the preview
       it was leaving and only then enter the neighbour. The neighbour builds
       its own preview hidden and reveals it a raster settle later (a layout
       frame, then a re-raster in sliced tasks), so every glide showed the grid
       through for a frame or more with no enlarged card at all — found 25
       September 2026 (flicker hunt, `hover-handoff-gap`).

       A catalog that opts in (`handoff: true`) keeps the old preview on
       screen instead, pointer-transparent so the grid beneath gets its real
       boundary events and the old popup cannot hand off again, and lets it go
       in the same task that reveals the next preview (`releaseHandedOff`). It
       also lets it go when nothing is left to reveal one: each hover the
       catalog starts is counted (`beginHover`), and when the last one ends
       without a reveal — its settle failed, the pointer left, a newer hover
       superseded it and then left too — the handed-off preview goes with it.
       A hover that never ends is covered by the cap below.

       ONE ELEMENT NAMES THE PREVIEW. Kept on screen, the preview being left
       also kept its id, so for up to the cap two elements were
       `#card-hover-preview` — invalid HTML — and every reader by id (the
       shared clear in cards.js, the catalogs' own clears, the specs) got the
       first of them: the one being left. It gives the id up when it is handed
       off, before the neighbour hears of it, so the id only ever names the
       preview being built. The one being left is still a `.card-hover-preview`
       and says `data-hover-handoff`; a tear-down that must take it too calls
       `releaseHandedOff` (or selects it by that attribute). Its look must not
       change with the id: each id-keyed rule a handed-off preview can reach
       (cards.css, foundry.css) names `.card-hover-preview[data-hover-handoff]`
       as well, and this change never ships without them —
       tests/unit/ui/hoverHandoff.test.js, "a handed-off preview keeps the
       look the id gave it". */
    const HANDOFF_HOLD_CAP_MS = 1000;
    /** @type {Map<HTMLElement, { release: () => void, timer: ReturnType<typeof setTimeout> }>} */
    const handedOff = new Map();
    let pendingHovers = 0;

    /** @param {Element | null | undefined} preview */
    function isHandedOff(preview) {
        return Boolean(preview) && handedOff.has(/** @type {HTMLElement} */ (preview));
    }

    function hasHandedOff() {
        return handedOff.size > 0;
    }

    /** Whether a mouseenter is a hand-off from a neighbour's preview. */
    function isHandoffEnter(event) {
        return Boolean(/** @type {any} */ (event)?.moonfallHandoff);
    }

    /** @param {HTMLElement} preview */
    function releaseOne(preview) {
        const entry = handedOff.get(preview);
        if (!entry) return;
        handedOff.delete(preview);
        clearTimeout(entry.timer);
        if (preview.isConnected) entry.release();
    }

    /** Take down every handed-off preview. A reveal calls this in its own task. */
    function releaseHandedOff() {
        Array.from(handedOff.keys()).forEach(releaseOne);
    }

    /**
     * A catalog starts a hover that may reveal a preview. Call the returned
     * function once, when that hover reveals or gives up.
     * @returns {() => void}
     */
    function beginHover() {
        pendingHovers += 1;
        let ended = false;
        return () => {
            if (ended) return;
            ended = true;
            pendingHovers -= 1;
            if (pendingHovers === 0) releaseHandedOff();
        };
    }

    /**
     * The preview the pointer can still own: the one carrying the id. A
     * handed-off preview has given the id up (see "ONE ELEMENT NAMES THE
     * PREVIEW"), so this is `getElementById`, kept as the catalogs' one
     * question for it.
     */
    function currentPreview(id = 'card-hover-preview') {
        const preview = document.getElementById(id);
        return preview && !isHandedOff(preview) ? preview : null;
    }

    /**
     * @param {HTMLElement} preview
     * @param {Element} wrapper
     * @param {() => void} release
     */
    function handOff(preview, wrapper, release) {
        if (!handedOff.has(preview)) {
            preview.dataset.hoverHandoff = 'handed-off';
            // Before the enter below: the neighbour's clear and build must
            // find only its own preview by the id.
            preview.removeAttribute('id');
            preview.style.setProperty('pointer-events', 'none', 'important');
            const timer = setTimeout(() => releaseOne(preview), HANDOFF_HOLD_CAP_MS);
            handedOff.set(preview, { release, timer });
        }
        const enter = new MouseEvent('mouseenter', { bubbles: false });
        /** @type {any} */ (enter).moonfallHandoff = { from: preview };
        wrapper.dispatchEvent(enter);
        // The neighbour declined the hover (nothing began): nothing will
        // replace the old preview, so it goes now.
        if (pendingHovers === 0) releaseHandedOff();
    }

    /* Read per call, never cached: an attached mouse, a devtools emulation
       toggle, or a 2-in-1 folding into tablet mode all flip this mid-session. */
    function isCoarsePointer() {
        try {
            return window.matchMedia?.('(pointer: coarse)')?.matches === true;
        } catch (_) {
            return false;
        }
    }

    /**
     * @param {HTMLElement} preview   the popup that currently owns the hover
     * @param {object}   options
     * @param {HTMLElement} options.source    element the preview was opened from
     * @param {string}   options.selector     identifies a hoverable card
     * @param {Function} options.clear        tears the current preview down
     * @param {HTMLElement} [options.root]    confine hand-off to this container
     * @param {boolean}  [options.handoff]    keep this preview until the
     *   neighbour's is revealed (see "A HAND-OFF, NOT A TEAR-DOWN")
     * @param {() => void} [options.release] takes this preview down once it
     *   has been handed off; defaults to removing it
     * @returns {Function} unbind
     */
    function bind(preview, {
        source, selector, clear, root = null, handoff = false, release = null,
    } = /** @type {any} */ ({})) {
        if (!preview || !selector || typeof clear !== 'function') return () => {};

        /* Hand-off is a MOUSE affordance and only a mouse affordance: it exists
           because a pointer gliding to a neighbour never reaches that
           neighbour's `mouseenter`. A touch device has no glide — the tap latch
           owns inspection there — so on a coarse pointer this listener can only
           do harm, and it did: its `clear()` tore down a latched preview and
           re-opened it on whatever card happened to sit under the finger,
           reported as "pressing add hovers a different card". Per-event
           pointerType guards were not enough, because a phone still emits
           mouse-typed moves once the suppression window lapses. Do not bind. */
        if (isCoarsePointer()) return () => {};

        const releasePreview = typeof release === 'function' ? release : () => preview.remove();
        let handedTo = null;

        const onMove = (event) => {
            // Touch owns tap-to-inspect; a synthesized move must not re-target.
            if (event.pointerType === 'touch') return;
            if (TouchPreview?.isSuppressingMouse?.()) return;
            if (!preview.isConnected) return;

            const beneath = document.elementsFromPoint?.(event.clientX, event.clientY) || [];
            for (const node of beneath) {
                if (node === preview || preview.contains(node)) continue;
                const wrapper = node.closest?.(selector);
                if (!wrapper) continue;
                if (root && !root.contains(wrapper)) continue;
                // Re-entering our own source would tear this popup down and
                // rebuild it on every pointermove: a flicker plus a raster
                // rebuild a frame.
                if (wrapper === source) return;
                if (handoff) {
                    // The same neighbour again, while its preview is still
                    // being built: it already has the hover.
                    if (wrapper === handedTo) return;
                    handedTo = wrapper;
                    handOff(preview, wrapper, releasePreview);
                    return;
                }
                clear();
                wrapper.dispatchEvent(new MouseEvent('mouseenter', { bubbles: false }));
                return;
            }
        };

        preview.addEventListener('pointermove', onMove);
        return () => preview.removeEventListener('pointermove', onMove);
    }

    HoverHandoff = {
        bind,
        beginHover,
        releaseHandedOff,
        isHandedOff,
        hasHandedOff,
        isHandoffEnter,
        currentPreview,
    };
})();

export { HoverHandoff };
